/**
 * Pure helpers the root uses to turn the host's staged files into a picker source
 * (FRAME_PICKER_DESIGN.md §5.1 the rendition route, §6.3 step 1–2, §6.4 "find the staged file in
 * `host.stagedFiles$`").
 *
 * No React, no DOM, no network. The rendition URL is composed the way ironman registered it
 * (`UploadsController.rendition`, `@GetMapping("/{uploadedFileId}/renditions/{kind}")`), from the
 * facts an `UploadInProgressFile` already carries once its upload response has landed: the
 * 24-hex id in `OriginalInputFile.location`, the upload JWT in `.token`, and the processor's
 * `uploadDomain` (a rendition lives on the disk of the host that took the upload). Nothing here
 * imports `components/studio/**` (Global Constraints); the same derivation is spelled out again.
 */
import type { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { devProxyOrigin, isDevProxyEnabled } from '../../../services/dev-proxy';
import { ANIMATED_IMAGE_PICKER, PICKER_ANIMATED_FROM_TYPES } from '../picker-gate';
import type { StagedFilesSnapshot } from '../host';
import type { PlaybackSource } from '../media/caps';
import { createTimeline, cutToLastFrame, type ProbeFacts, type Timeline } from '../media/timeline';
import type { PickerSource } from '../state/picker-state';
import { canRestoreFile, type StagedFileRef } from '../state/persistence';
import type { InputFileInfo } from '../../../types/file-upload.types';

/** The video extensions the picker treats as a source (the page gate already limits `fromMedia` to VIDEO). */
export const PICKER_VIDEO_EXTENSIONS: ReadonlySet<string> = new Set([
    'mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', 'wmv', 'flv', 'mpg', 'mpeg', '3gp', 'ts', 'mts', 'm2ts', 'ogv', 'hevc',
]);

/**
 * R14 (slice C, BUG-003): the animated images the picker opens as clips (the edge's `preview` rendition is an mp4 of every
 * frame; ironman ≥ `1ac91e8d` picks real frames from them) -- folded into `isVideoUpload` / `isVideoFile` ONLY under
 * `picker-gate.ts#ANIMATED_IMAGE_PICKER`.
 */
export const PICKER_ANIMATED_IMAGE_EXTENSIONS: ReadonlySet<string> = PICKER_ANIMATED_FROM_TYPES;

export const EDGE_PATH_PREFIX = '/v2/edge';

export function extensionOf(name: string | null | undefined): string {
    const n = name || '';
    const i = n.lastIndexOf('.');
    return i > 0 ? n.slice(i + 1).toLowerCase() : '';
}

/** The `File` (or Blob) behind a staged entry, or null (a stub entry restored from a record). */
export function localFileOf(upload: UploadInProgressFile<any> | null | undefined): File | null {
    const raw: unknown = upload?.file;
    return typeof Blob !== 'undefined' && raw instanceof Blob ? (raw as File) : null;
}

export function fileNameOf(upload: UploadInProgressFile<any> | null | undefined): string {
    const raw: any = upload?.file;
    const response = upload?.getResponse?.();
    return (typeof raw?.name === 'string' && raw.name) || response?.originalName || '';
}

/** Is this name / MIME type an animated image the picker can open (`image/gif|webp|apng`, or the extension)? */
export function isAnimatedImageName(name: string | null | undefined, type?: string | null): boolean {
    if (typeof type === 'string' && /^image\/(gif|webp|apng)$/i.test(type.trim())) return true;
    return PICKER_ANIMATED_IMAGE_EXTENSIONS.has(extensionOf(name));
}

/**
 * Is this staged entry a video, by MIME type first and extension second? R14 (BUG-003): or an animated image, while
 * `ANIMATED_IMAGE_PICKER` is on (`animated` is a parameter so both values are tested; callers never pass it).
 */
export function isVideoUpload(upload: UploadInProgressFile<any> | null | undefined, animated: boolean = ANIMATED_IMAGE_PICKER): boolean {
    const raw: any = upload?.file;
    if (typeof raw?.type === 'string' && raw.type.startsWith('video/')) return true;
    const name = fileNameOf(upload);
    if (PICKER_VIDEO_EXTENSIONS.has(extensionOf(name))) return true;
    return animated && !!upload && isAnimatedImageName(name, typeof raw?.type === 'string' ? raw.type : null);
}

/**
 * The staged file the picker works on: the one a restored record names when it is still staged,
 * else the first video in the snapshot, else null. `fileWithMetadataList` is the list that has
 * upload responses; the fake list is only the pre-upload placeholder row.
 */
export function selectVideoFile(snapshot: StagedFilesSnapshot | null | undefined, preferredUuid: string | null, animated: boolean = ANIMATED_IMAGE_PICKER): UploadInProgressFile<any> | null {
    const list = snapshot?.fileWithMetadataList ?? [];
    if (preferredUuid) {
        const hit = list.find((f) => f?.uuid === preferredUuid);
        if (hit) return hit;
    }
    return list.find((f) => isVideoUpload(f, animated)) ?? null;
}

/**
 * R14 (slice J, BUG-024 / review-6 BUG-043): which of a record's `staged` refs are NOT in the host's snapshot any more
 * (Back from `/downloads` remounted the page without its list): `restorable` can be rebuilt on the server
 * (`root/restored-upload.ts#restoredUploadOf`, in the record's order); `expired` cannot (saved before the upload answered)
 * and are NAMED to the user rather than dropped in silence. Refs the snapshot still holds are neither. Pure.
 */
export function missingStagedRefs(
    refs: ReadonlyArray<StagedFileRef> | null | undefined,
    staged: ReadonlyArray<UploadInProgressFile<any> | null | undefined> | null | undefined,
): { restorable: StagedFileRef[]; expired: StagedFileRef[] } {
    const present = new Set((staged ?? []).map((f) => f?.uuid).filter((u): u is string => typeof u === 'string' && u !== ''));
    const restorable: StagedFileRef[] = [];
    const expired: StagedFileRef[] = [];
    const seen = new Set<string>();
    for (const ref of refs ?? []) {
        if (!ref?.uuid || present.has(ref.uuid) || seen.has(ref.uuid)) continue;
        seen.add(ref.uuid);
        (canRestoreFile(ref) ? restorable : expired).push(ref);
    }
    return { restorable, expired };
}

/** R14 (parent's carried item): the Files head's two numbers -- `{ videos, unreadable }`, counted apart. */
export interface StagedCounts {
    /** Staged clips the picker can (or may yet) read. */
    videos: number;
    /** Staged files the edge described as no video and whose preview never came (`broken` / `background.state === 'broken'`). */
    unreadable: number;
}

/**
 * R14 (parent's carried item, files-pane `countLabel`): "1 video · 1 file couldn't be read" needs the unreadable rows counted
 * APART from the videos -- today the head says "2 videos" over a row that reads "We couldn't read …". `isUnreadable(uuid)`
 * is the root's knowledge (`state.broken` for the stage clip, `background.get(uuid)?.state === 'broken'` off it). Pure.
 */
export function stagedCounts(files: ReadonlyArray<UploadInProgressFile<any> | null | undefined>, isUnreadable: (uuid: string) => boolean): StagedCounts {
    let unreadable = 0;
    let videos = 0;
    for (const f of files) {
        if (!f) continue;
        if (typeof f.uuid === 'string' && f.uuid !== '' && isUnreadable(f.uuid)) unreadable++;
        else videos++;
    }
    return { videos, unreadable };
}

/** The 24-hex `uploaded-files` id out of a safe name like `…-x-<id>.mp4` (or `…-x-<id>-<n>.mp4`). */
export function uploadedFileIdOf(location: string | null | undefined): string | null {
    if (!location) return null;
    const marker = location.lastIndexOf('-x-');
    if (marker < 0) return null;
    const tail = location.slice(marker + 3);
    const dot = tail.lastIndexOf('.');
    const base = dot > 0 ? tail.slice(0, dot) : tail;
    const id = base.includes('-') ? base.split('-')[0] : base;
    return /^[0-9a-f]{24}$/i.test(id) ? id : null;
}

/**
 * `https://<uploadDomain>/v2/edge`; an empty domain yields the same-origin prefix. Under `next dev`
 * the edge is addressed through the dev server's own origin (`services/dev-proxy.ts`, the one place
 * that decides this -- `ApiConfig.originForDomain` does the same for every other endpoint): the edge
 * answers `Access-Control-Allow-Origin: https://www.xconvert.com`, so an absolute rendition URL from
 * `localhost` is CORS-blocked and a `<video crossorigin>` on it reports a format error (measured
 * 2026-09-19 on the xps dev server; round 3 R9a). Never active in a deployed build.
 */
export function edgeBaseFor(uploadDomain: string | null | undefined): string {
    const trimmed = (uploadDomain || '').trim().replace(/\/+$/, '');
    if (!trimmed) return EDGE_PATH_PREFIX;
    const origin = isDevProxyEnabled() ? devProxyOrigin(trimmed) : (/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return origin.endsWith(EDGE_PATH_PREFIX) ? origin : `${origin}${EDGE_PATH_PREFIX}`;
}

export type RenditionKind = 'poster' | 'preview' | 'strip' | 'strip-dense' | 'thumb';

/** The three facts a rendition is addressed with — off a live upload or a stored record (R9a). */
export interface RenditionAddress {
    location?: string | null;
    token?: string | null;
    uploadDomain?: string | null;
}

/** The registered route from the facts alone, or null when the id or the token is missing. */
export function renditionUrlFrom(address: RenditionAddress | null | undefined, kind: RenditionKind): string | null {
    if (!address?.token) return null;
    const id = uploadedFileIdOf(address.location);
    if (!id) return null;
    const base = edgeBaseFor(address.uploadDomain);
    return `${base}/uploads/${encodeURIComponent(id)}/renditions/${kind}?token=${encodeURIComponent(address.token)}`;
}

/** The processor record on a staged upload, whichever shape the entry carries. */
export function processorMetadataOf(upload: UploadInProgressFile<any> | null | undefined): any {
    return upload?.getMetadata?.()?.getProcessorMetadata?.(0) ?? (upload as any)?.metadata?.processorMetadataList?.[0] ?? null;
}

/**
 * The rendition URL for a staged upload, or null before the upload response has landed (no id,
 * no token yet — the picker falls back to `{ kind: 'none' }` until it has).
 */
export function renditionUrlOf(upload: UploadInProgressFile<any> | null | undefined, kind: RenditionKind): string | null {
    const response = upload?.getResponse?.();
    const processor = processorMetadataOf(upload);
    return renditionUrlFrom({ location: response?.location, token: response?.token, uploadDomain: processor?.uploadDomain }, kind);
}

/**
 * The server's copy of the ORIGINAL upload (R9a): `<edge>/downloads/uploaded-file/<safeName>?token=`,
 * the route `FileUpload.getInputDownloadURLsIfAvailable` builds off the same processor record. For a
 * clip rebuilt from a record it is the FALLBACK rung since T2 (EDITOR_REOPEN_PLAN R-B): it was first
 * while `preview` renditions answered 404 on QA (2026-09-19); measured 2026-09-25 the `preview` answers
 * 202 while building, then 206 on ranged GETs, same 750 frames / 25 fps at half the bytes, while this
 * route ignores `Range` (a 10 MB re-download). See `restorePreviewStep`. Null without the processor
 * record, the safe name or the token.
 */
export function uploadedFileUrlOf(upload: UploadInProgressFile<any> | null | undefined): string | null {
    const response = upload?.getResponse?.();
    const processor = processorMetadataOf(upload);
    const safeName: unknown = processor?.fileMetadata?.safeName ?? response?.location;
    const endpoint: unknown = processor?.uploadedFileDownloadEndpoint;
    if (!response?.token || !processor?.uploadDomain || typeof safeName !== 'string' || !safeName || typeof endpoint !== 'string' || !endpoint) return null;
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${edgeBaseFor(processor.uploadDomain)}${path}/${encodeURIComponent(safeName)}?token=${encodeURIComponent(response.token)}`;
}

/**
 * T2 (EDITOR_REOPEN_PLAN R-B): the restore ladder's first rung for a clip rebuilt from a record is the `preview` rendition
 * (540p, ranged, same frame grid). The edge answers 202 (or 503 / 429) while it still renders it -- about 6 s after the
 * upload (Graylog `proxy_done kind=preview`) -- so the root asks again on this ladder (a `Retry-After` wins), for at most
 * `RESTORE_PREVIEW_WAIT_MS` in all, poster and strip on the stage meanwhile; a 404, any other answer, a network / CORS
 * failure or the time spent → the original rung (`uploadedFileUrlOf`). 200 / 206 → play it.
 */
export const RESTORE_PREVIEW_RETRY_MS: readonly number[] = Object.freeze([1000, 2000, 3000, 5000]);
export const RESTORE_PREVIEW_WAIT_MS = 20_000;
/** And at most this many asks, whatever the clock says (a stalled clock never loops). */
export const RESTORE_PREVIEW_MAX_ASKS = 12;
/** Never ask again sooner than this, whatever `Retry-After` says (a `Retry-After: 0` is not a busy loop). */
const RESTORE_PREVIEW_MIN_WAIT_MS = 250;

export type RestorePreviewStep = { kind: 'play' } | { kind: 'wait'; ms: number } | { kind: 'original' };

/** What the `preview` rung's `status` (0 = no answer the page may read) means after `attempt` asks and `waitedMs` of waiting. Pure. */
export function restorePreviewStep(status: number, retryAfterMs: number | null, attempt: number, waitedMs: number): RestorePreviewStep {
    if (status === 200 || status === 206) return { kind: 'play' };
    if (status !== 202 && status !== 503 && status !== 429) return { kind: 'original' };
    const left = RESTORE_PREVIEW_WAIT_MS - Math.max(0, waitedMs);
    if (left <= 0 || attempt + 1 >= RESTORE_PREVIEW_MAX_ASKS) return { kind: 'original' };
    const rung = RESTORE_PREVIEW_RETRY_MS[Math.min(Math.max(0, attempt), RESTORE_PREVIEW_RETRY_MS.length - 1)];
    const asked = typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs) && retryAfterMs >= 0 ? retryAfterMs : rung;
    return { kind: 'wait', ms: Math.min(left, Math.max(RESTORE_PREVIEW_MIN_WAIT_MS, asked)) };
}

/**
 * R5-JC-01: the STAGE's `preview` ladder (a clip this browser cannot play). The edge renders the preview on its owner's video lane,
 * which is serial per identity: behind another session's long preview (a 64-min webinar: 130 s, Graylog `proxy_done kind=preview
 * ms=131534`, 04:38Z) an AVI's 0.7 s preview waited 96 s, and the old blind ladder (2 / 5 / 10 / 20 s of element probes, ~45 s)
 * had given up for the session. Now the edge is ASKED (one ranged byte, `renditionStatus`): 202 / 503 / 429 = still owed, so wait
 * (a `Retry-After` wins) for up to `PREVIEW_OWED_WAIT_MS`; any other answer = the element says (200 / 206 plays; a failure is
 * retried `PREVIEW_ELEMENT_TRIES` times on the old 2 / 5 / 10 / 20 s backoff, which a 404 also ends on). Bounded by time AND asks. Pure.
 */
export const PREVIEW_AWAIT_BACKOFF_MS: readonly number[] = Object.freeze([1000, 2000, 3000, 5000, 8000, 10000]);
export const PREVIEW_OWED_WAIT_MS = 240_000;
export const PREVIEW_AWAIT_MAX_ASKS = 40;
/** The element's own tries on a ready (or unknown) preview: the first plus the old ladder's four waits (2 / 5 / 10 / 20 s). */
export const PREVIEW_ELEMENT_TRIES = 5;
export const PREVIEW_ELEMENT_BACKOFF_MS: readonly number[] = Object.freeze([2000, 5000, 10000, 20000]);
export type PreviewAwaitStep = { kind: 'play' } | { kind: 'wait'; ms: number } | { kind: 'give-up' };
/** The edge's answer for the preview (`status`, 0 = none the page may read) at ask `ask`, after `waitedMs`. */
export function previewAwaitStep(status: number, retryAfterMs: number | null, ask: number, waitedMs: number): PreviewAwaitStep {
    const left = PREVIEW_OWED_WAIT_MS - Math.max(0, waitedMs);
    if (left <= 0 || ask >= PREVIEW_AWAIT_MAX_ASKS) return { kind: 'give-up' };
    // Only "still owed" waits. Ready -- and any other answer (0 = none readable; a 404 is terminal by contract, but the element's own
    // bounded tries cost nothing more than the old ladder did) -- lets the element say (`previewElementRetry` bounds it).
    if (status !== 202 && status !== 503 && status !== 429) return { kind: 'play' };
    const rung = PREVIEW_AWAIT_BACKOFF_MS[Math.min(Math.max(0, ask), PREVIEW_AWAIT_BACKOFF_MS.length - 1)];
    const asked = typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs) && retryAfterMs >= 0 ? Math.max(1000, retryAfterMs) : rung;
    return { kind: 'wait', ms: Math.min(left, asked) };
}
/** The element failed on a preview the edge called ready (or could not say): try again after a backoff, `PREVIEW_ELEMENT_TRIES` in all. */
export function previewElementRetry(failures: number, waitedMs: number): PreviewAwaitStep {
    const left = PREVIEW_OWED_WAIT_MS - Math.max(0, waitedMs);
    if (failures >= PREVIEW_ELEMENT_TRIES || left <= 0) return { kind: 'give-up' };
    return { kind: 'wait', ms: Math.min(left, PREVIEW_ELEMENT_BACKOFF_MS[Math.min(Math.max(0, failures - 1), PREVIEW_ELEMENT_BACKOFF_MS.length - 1)]) };
}

/** `PlaybackSource.kind` → the state's `source` word (§6.4 / §8 `FRAMEPICKER_SOURCE.kind`). */
export function sourceKindOf(source: PlaybackSource | null | undefined): PickerSource {
    if (!source) return 'none';
    return source.kind === 'local' ? 'original' : source.kind === 'rendition' ? 'preview' : 'none';
}

/**
 * The probe's answer → a `Timeline`; null when the edge reported no usable duration. R14 (BUG-003): an animated image's
 * probe carries `numberOfFrames` and `framerate` (and, since ironman `1ac91e8d`, `duration`); under `ANIMATED_IMAGE_PICKER`
 * a missing `duration` falls back to `numberOfFrames / framerate`.
 */
export function timelineOf(info: Partial<InputFileInfo> | null | undefined, animated: boolean = ANIMATED_IMAGE_PICKER): Timeline | null {
    let duration = Number(info?.duration);
    if (!(Number.isFinite(duration) && duration > 0) && animated) {
        const frames = Number(info?.numberOfFrames);
        const rate = Number(info?.framerate);
        if (Number.isFinite(frames) && frames > 0 && Number.isFinite(rate) && rate > 0) duration = frames / rate;
    }
    if (!Number.isFinite(duration) || duration <= 0) return null;
    // SMALL-1: the edge's `last_frame_time` (sent only when the video ends before `duration`) ends the scrubber on the real last frame.
    return cutToLastFrame(createTimeline(duration, Number(info?.framerate), probeFactsOf(info)), info?.last_frame_time);
}

/**
 * SMALL-1 (prod fixes 2026-10-02): the probe's answer with `duration` / `frame_count` cut to the video's real last frame when the edge
 * said where it is (`last_frame_time`), exactly as `timelineOf` cuts the timeline -- so everything that persists or re-reads the
 * info (the record's staged ref, the card's "Frame n of N" badge, a restored clip) counts the same frames as the scrubber. The
 * same object when nothing is cut. Idempotent.
 */
export function infoCutToLastFrame<T extends Partial<InputFileInfo>>(info: T): T;
export function infoCutToLastFrame<T extends Partial<InputFileInfo>>(info: T | null | undefined): T | null | undefined;
export function infoCutToLastFrame<T extends Partial<InputFileInfo>>(info: T | null | undefined): T | null | undefined {
    if (!info || info.last_frame_time === undefined || info.last_frame_time === null) return info;
    const tl = timelineOf(info);
    if (!tl?.endsEarly) return info;
    if (info.duration === tl.duration && info.frame_count === tl.frameCount) return info;
    return { ...info, duration: tl.duration, frame_count: tl.frameCount };
}

/**
 * R14 (slice L, BUG-016, R14-SRV5): the probe's frame facts beyond `duration` / `framerate` -- `frame_count` (the frames
 * really present), `avg_framerate` (timestamp-derived) and `frame_rate_mode` (the server's CFR / VFR verdict). Each is
 * `undefined` when the edge did not say (an older edge omits all three); a mode other than the two words is ignored.
 */
export function probeFactsOf(info: Partial<InputFileInfo> | null | undefined): ProbeFacts {
    const mode = info?.frame_rate_mode;
    return {
        frameCount: positiveOrUndefined(info?.frame_count),
        avgFps: positiveOrUndefined(info?.avg_framerate),
        mode: mode === 'VFR' || mode === 'CFR' ? mode : undefined,
    };
}

/**
 * R14 (slice L, BUG-015, R14-SRV5): the size a frame is SHOWN at -- `display_width` × `display_height` (coded × the sample
 * aspect ratio; 1024 × 576 for a 720 × 576 anamorphic MPEG-2) when the edge said both, else the coded `width` × `height`,
 * else null. The Input picture's aspect, the Output caption and the exported frame all follow this one.
 */
export function displaySizeOf(info: Partial<InputFileInfo> | null | undefined): { width: number; height: number } | null {
    const dw = positiveOrUndefined(info?.display_width);
    const dh = positiveOrUndefined(info?.display_height);
    if (dw !== undefined && dh !== undefined) return { width: dw, height: dh };
    const w = positiveOrUndefined(info?.width);
    const h = positiveOrUndefined(info?.height);
    return w !== undefined && h !== undefined ? { width: w, height: h } : null;
}

/** Source aspect (w/h) for the grid and sheet cells -- the DISPLAY size's (R14 slice L, BUG-015); 16/9 when unknown. */
export function aspectOf(info: Partial<InputFileInfo> | null | undefined): number {
    const size = displaySizeOf(info);
    return size ? size.width / size.height : 16 / 9;
}

function positiveOrUndefined(raw: unknown): number | undefined {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}
